/**
 * @file Browser-safe wire shapes for the operator-facing secret
 *       rotation surface. Mirrors the `SettingDefinition` shape in
 *       `@labcraft/postgres/shared/settings` so the admin SPA can
 *       render both with the same panel skeleton.
 * @module @labcraft/openbao/shared/secret
 *
 * Pure Zod — no node-only deps, nothing that would pull `node:https`
 * or the OpenBao client into a SPA bundle. The actual rotation
 * runtime lives on the admin daemon's Secrets service; this file is
 * the contract slice that crosses the server/browser boundary.
 *
 * Imported via the package's `./shared` export so the bundler's
 * resolver never reaches the root barrel (which transitively imports
 * `node:https` for cert auth).
 */

import { z } from 'zod'

////////////////////////////////////////////////////////////////////////////////
// Secret type + schema definition
////////////////////////////////////////////////////////////////////////////////

/**
 * Per-secret declaration. The daemon owning the secret publishes one
 * of these per key it expects to read from Bao; admin's Secrets panel
 * renders the union of every daemon's declarations.
 *
 * Distinct from `SettingDefinition` in two ways:
 *   1. Secrets are write-only from the panel — `value` never crosses
 *      the wire on `list()`. The shape carries metadata only.
 *   2. The `path` is the Bao KV path the value lives at
 *      (`account/providers/google/client_secret`, for example), not
 *      a dotted setting key. The admin's AppRole writes to
 *      `kv/<path>` directly.
 *
 * @property path - Bao KV path under the implicit `kv/` mount,
 *   e.g. `account/providers/google/client_secret`. Stable across
 *   releases; renaming a path orphans previous rotations (and the
 *   daemon that reads it).
 * @property daemon - Daemon identifier that owns the secret
 *   (`account`, `email`, `www`). Used as the sub-panel header.
 * @property label - Short operator-facing label for the row, e.g.
 *   "Google OAuth client secret".
 * @property description - One-paragraph help text, plain prose.
 *   Shown beneath the rotation widget in the admin UI. Document why
 *   the secret matters and how the consumer reads it so an operator
 *   rotating in a hurry has enough context to do the right thing.
 */
export const SecretDefinitionSchema = z.object({
  path: z.string().min(1),
  daemon: z.string().min(1),
  label: z.string().min(1),
  description: z.string().min(1),
})

/** Parsed `SecretDefinition`. */
export type SecretDefinition = z.infer<typeof SecretDefinitionSchema>

////////////////////////////////////////////////////////////////////////////////
// Wire shape: list result
////////////////////////////////////////////////////////////////////////////////

/**
 * Wire shape returned by `GET /api/secrets`. Carries the per-secret
 * definition plus the most recent rotation metadata — never the
 * value itself. Empty `lastRotated` (`null`) means Bao has no
 * version for the path yet.
 *
 * @property definition - Per-secret declaration; see above.
 * @property lastRotated - ISO-8601 timestamp of the latest write to
 *   the KV path, or `null` when no version exists.
 * @property version - Latest version number from Bao's KV v2
 *   metadata, or `null` when no version exists.
 */
export type SecretListEntry = {
  definition: SecretDefinition
  lastRotated: string | null
  version: number | null
}

////////////////////////////////////////////////////////////////////////////////
// Wire shape: rotate request
////////////////////////////////////////////////////////////////////////////////

/**
 * Body of `POST /api/secrets/rotate`. The admin server writes the
 * raw `value` into Bao's KV under the implicit `kv/<path>` mount.
 *
 * @property path - Bao KV path to rotate. Must match an entry from
 *   the panel's registered SecretDefinition[].
 * @property value - New secret value, written verbatim. Bao's KV v2
 *   bumps the version; old versions remain available until
 *   destroyed.
 */
export const RotateSecretRequestSchema = z.object({
  path: z.string().min(1),
  value: z.string().min(1),
})

/** Parsed `RotateSecretRequest`. */
export type RotateSecretRequest = z.input<typeof RotateSecretRequestSchema>

////////////////////////////////////////////////////////////////////////////////
// Wire shape: rotate response
////////////////////////////////////////////////////////////////////////////////

/**
 * Response from `POST /api/secrets/rotate`. Bao's audit log
 * captures the named operator + timestamp; the panel's history view
 * sources from there (no Postgres history table).
 *
 * @property path - Echoes the requested path.
 * @property version - New version number Bao minted.
 * @property rotatedAt - ISO-8601 timestamp Bao recorded for the
 *   write (server time).
 */
export type RotateSecretResponse = {
  path: string
  version: number
  rotatedAt: string
}
